import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  IconButton, 
  TextField, 
  InputAdornment, 
  Button, 
  Menu, 
  MenuItem, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  CircularProgress, 
  Alert,
  Divider
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { supabase, ensureChatTablesExist } from '../supabaseClient';

const ChatHistory = ({ onSelectChat, onNewChat, session }) => {
  const [chatSessions, setChatSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    if (session?.user?.id) {
      const init = async () => {
        try {
          // Check if tables exist
          const tablesExist = await ensureChatTablesExist();
          
          if (tablesExist) {
            // Then fetch sessions
            fetchChatSessions();
          } else {
            setError('Sohbet geçmişi özelliği şu anda kullanılamıyor. Lütfen daha sonra tekrar deneyin.');
            setLoading(false);
          }
        } catch (error) {
          console.error('Error initializing chat history:', error);
          setError('Sohbet geçmişi yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
          setLoading(false);
        }
      };
      
      init();
    }
  }, [session, showArchived]);

  const fetchChatSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try to fetch chat sessions
      const { data, error } = await supabase
        .from('chat_sessions')
        .select('*')
        .eq('user_id', session.user.id)
        .eq('is_archived', showArchived)
        .order('is_pinned', { ascending: false })
        .order('updated_at', { ascending: false });
      
      if (error) {
        console.error('Error details:', error);
        // Don't throw, just set empty array
        setChatSessions([]);
        if (error.code === '42P01') {
          setError('Sohbet geçmişi tablosu bulunamadı. Lütfen yönetici ile iletişime geçin.');
        } else {
          setError('Sohbet geçmişi yüklenirken bir hata oluştu.');
        }
      } else {
        setChatSessions(data || []);
      }
    } catch (error) {
      console.error('Error fetching chat sessions:', error);
      setChatSessions([]);
      setError('Sohbet geçmişi yüklenirken beklenmeyen bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, session) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedSession(session);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenRenameDialog = () => {
    setNewTitle(selectedSession.title);
    setRenameDialogOpen(true);
    handleMenuClose();
  };

  const handleRenameSession = async () => {
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('chat_sessions')
        .update({ 
          title: newTitle,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedSession.id);
      
      if (error) throw error;
      
      // Update local state
      setChatSessions(prev => 
        prev.map(session => 
          session.id === selectedSession.id 
            ? { ...session, title: newTitle, updated_at: new Date().toISOString() } 
            : session
        )
      );
      
      setRenameDialogOpen(false);
      
    } catch (error) {
      console.error('Error renaming chat session:', error);
      setError('Sohbet yeniden adlandırılırken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteSession = async () => {
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('chat_sessions')
        .delete()
        .eq('id', selectedSession.id);
      
      if (error) throw error;
      
      // Update local state
      setChatSessions(prev => 
        prev.filter(session => session.id !== selectedSession.id)
      );
      
      setDeleteDialogOpen(false);
      
    } catch (error) {
      console.error('Error deleting chat session:', error);
      setError('Sohbet silinirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePin = async (event) => {
    event.stopPropagation();
    handleMenuClose();
    
    try {
      const isPinned = !selectedSession.is_pinned;
      
      const { error } = await supabase
        .from('chat_sessions')
        .update({ 
          is_pinned: isPinned,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedSession.id);
      
      if (error) throw error;
      
      // Update local state
      setChatSessions(prev => {
        const updated = prev.map(session => 
          session.id === selectedSession.id 
            ? { ...session, is_pinned: isPinned, updated_at: new Date().toISOString() } 
            : session
        );
        
        // Re-sort to put pinned items at top
        return [...updated].sort((a, b) => {
          if (a.is_pinned && !b.is_pinned) return -1;
          if (!a.is_pinned && b.is_pinned) return 1;
          return new Date(b.updated_at) - new Date(a.updated_at);
        });
      });
      
    } catch (error) {
      console.error('Error toggling pin status:', error);
      setError('Sabitleme durumu değiştirilirken bir hata oluştu.');
    }
  };

  const handleToggleArchive = async () => {
    handleMenuClose();
    
    try {
      const isArchived = !selectedSession.is_archived;
      
      const { error } = await supabase
        .from('chat_sessions')
        .update({ 
          is_archived: isArchived,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedSession.id);
      
      if (error) throw error;
      
      // Update local state - remove from current view
      setChatSessions(prev => 
        prev.filter(session => session.id !== selectedSession.id)
      );
      
    } catch (error) {
      console.error('Error toggling archive status:', error);
      setError('Arşivleme durumu değiştirilirken bir hata oluştu.');
    }
  };

  const filteredSessions = chatSessions.filter(session => 
    session.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    session.last_message?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <TextField
          fullWidth
          placeholder="Sohbet ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Box>
      
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onNewChat}
        >
          Yeni Sohbet
        </Button>
        
        <Button
          fullWidth
          variant="text"
          startIcon={showArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
          onClick={() => setShowArchived(!showArchived)}
          sx={{ mt: 1 }}
        >
          {showArchived ? 'Arşivlenmemiş Sohbetler' : 'Arşivlenmiş Sohbetler'}
        </Button>
      </Box>
      
      <Divider />
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
      ) : filteredSessions.length === 0 ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {searchTerm 
              ? 'Arama sonucu bulunamadı' 
              : showArchived 
                ? 'Arşivlenmiş sohbet bulunamadı' 
                : 'Henüz kaydedilmiş sohbet yok'}
          </Typography>
        </Box>
      ) : (
        <List sx={{ overflow: 'auto', flex: 1 }}>
          {filteredSessions.map((chatSession) => (
            <ListItem
              key={chatSession.id}
              button
              onClick={() => onSelectChat(chatSession.id)}
              secondaryAction={
                <IconButton edge="end" onClick={(e) => handleMenuOpen(e, chatSession)}>
                  <MoreVertIcon />
                </IconButton>
              }
              sx={{
                borderLeft: chatSession.is_pinned ? '3px solid' : 'none',
                borderColor: 'primary.main',
                pl: chatSession.is_pinned ? 1 : 2,
              }}
            >
              <ListItemIcon>
                {chatSession.is_pinned ? (
                  <PushPinIcon color="primary" />
                ) : (
                  <ChatIcon />
                )}
              </ListItemIcon>
              <ListItemText
                primary={chatSession.title}
                secondary={
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                  >
                    {chatSession.last_message || 'Boş sohbet'}
                  </Typography>
                }
                primaryTypographyProps={{ noWrap: true }}
                secondaryTypographyProps={{ 
                  component: 'span',
                  sx: { 
                    display: 'inline-block',
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }
                }}
              />
            </ListItem>
          ))}
        </List>
      )}
      
      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1.5,
            width: 200,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
            },
          },
        }}
      >
        <MenuItem onClick={handleTogglePin}>
          <ListItemIcon>
            {selectedSession?.is_pinned ? (
              <PushPinOutlinedIcon fontSize="small" />
            ) : (
              <PushPinIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {selectedSession?.is_pinned ? 'Sabitlemeyi Kaldır' : 'Sabitle'}
          </ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleOpenRenameDialog}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Yeniden Adlandır</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleToggleArchive}>
          <ListItemIcon>
            {selectedSession?.is_archived ? (
              <UnarchiveIcon fontSize="small" />
            ) : (
              <ArchiveIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {selectedSession?.is_archived ? 'Arşivden Çıkar' : 'Arşivle'}
          </ListItemText>
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handleOpenDeleteDialog} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Sil</ListItemText>
        </MenuItem>
      </Menu>
      
      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
        <DialogTitle>Sohbeti Yeniden Adlandır</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Sohbet Başlığı"
            fullWidth
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>İptal</Button>
          <Button onClick={handleRenameSession} variant="contained">
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Sohbeti Sil</DialogTitle>
        <DialogContent>
          <Typography>
            Bu sohbeti silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
          <Button onClick={handleDeleteSession} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatHistory;
